import React, { useEffect, useState } from 'react'
import { FeatureStatesProviderContextType, GeoFeatureDataType, GeoFeaturesDetailsType, RenameDataType, SelectedFeatureType } from '../utils/types'
import MissionModelService from '../../../service/MissionModelService'
import { useParams } from 'react-router-dom'
import { showToastMessage } from '../../../utils/commonFunctions';
import ModalsProvider from './ModalsProvider';

export const FeatureStatesProviderContext = React.createContext<FeatureStatesProviderContextType>({
    rename: null,
    setRename: () => { },
    renameFeature: async () => { },
    deleteFeature: async () => { },
    getGeoFeatures: async () => { },
    geoFeaturesDetails: {
        loading: true,
        allFeatures: [],
        filterFeatures: []
    },
    setGeoFeaturesDetails: () => { },
    selectedFeature: null,
    setSelectedFeature: () => { },
    setSidebarOpen: () => { },
    sidebarOpen: true,
})

const FeatureStatesProvider = ({ children }: { children: React.ReactNode }) => {
    const { mission_id } = useParams()
    const [rename, setRename] = useState<RenameDataType | null>(null)
    const [geoFeaturesDetails, setGeoFeaturesDetails] = useState<GeoFeaturesDetailsType>({
        loading: true,
        allFeatures: [],
        filterFeatures: []
    })
    const [selectedFeature, setSelectedFeature] = useState<SelectedFeatureType | null>(null)
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(true)

    const getGeoFeatures: () => Promise<void> = async () => {
        if (!mission_id) return
        try {
            const response = await MissionModelService.getAllGeoFeatures(mission_id)
            const features = response?.data || []
            setGeoFeaturesDetails({
                loading: false,
                allFeatures: features,
                filterFeatures: features,
            })
        } catch (error) {
            setGeoFeaturesDetails({
                loading: false,
                allFeatures: [],
                filterFeatures: [],
            })
        }
    }

    const deleteFeature: () => Promise<void> = async () => {
        const feature = selectedFeature?.data as GeoFeatureDataType
        try {
            const uniqueId = feature.areaOfInterestId
            const response = await MissionModelService.deleteFeature(uniqueId)
            const message = typeof response?.data?.message === 'string' ? response?.data?.message : 'GeoFeature deleted successfully'
            const updatedList = geoFeaturesDetails.allFeatures.filter((f: GeoFeatureDataType) => f.areaOfInterestId !== uniqueId)
            setGeoFeaturesDetails({ ...geoFeaturesDetails, allFeatures: updatedList, filterFeatures: updatedList })
            showToastMessage('success', message)
        } catch (error: any) {
            const message = typeof error?.response?.data?.message === 'string' ? error?.response?.data?.message : 'An error occurred while deleting the GeoFeature. Please try again.'
            showToastMessage('error', message)
        } finally {
            setSelectedFeature(null)
        }
    }

    const renameFeature: () => Promise<void> = async () => {
        setRename({
            ...rename!,
            loading: true
        })
        try {
            const uniqueId = rename!.uniqueId
            const name = rename!.name
            const response = await MissionModelService.renameFeature(uniqueId, name)
            const message = typeof response?.data?.message === 'string' ? response?.data?.message : 'GeoFeature renamed successfully'
            const allFeatures = geoFeaturesDetails.allFeatures || []
            const stationIndex = allFeatures.findIndex(g => g.areaOfInterestId === uniqueId);
            if (stationIndex !== -1) {
                allFeatures[stationIndex] = {
                    ...allFeatures[stationIndex],
                    name: name,
                };
            }
            setGeoFeaturesDetails({ ...geoFeaturesDetails, allFeatures: allFeatures, filterFeatures: allFeatures })
            setRename(null)
            showToastMessage('success', message)
        } catch (error: any) {
            const message = typeof error?.response?.data?.message === 'string' ? error?.response?.data?.message : 'An error occurred while renaming the GeoFeature. Please try again.'
            showToastMessage('error', message)
            setRename(null)
        }
    }

    useEffect(() => {
        getGeoFeatures()
    }, [mission_id])


    return (
        <FeatureStatesProviderContext.Provider value={{
            rename,
            setRename,
            sidebarOpen,
            renameFeature,
            deleteFeature,
            getGeoFeatures,
            setSidebarOpen,
            selectedFeature,
            setSelectedFeature,
            geoFeaturesDetails,
            setGeoFeaturesDetails,
        }}>
            <ModalsProvider>
                {children}
            </ModalsProvider>
        </FeatureStatesProviderContext.Provider>
    )
}

export default FeatureStatesProvider